/**
 * AttachmentChipStrip — renders the list of attached file chips with
 * an overflow heuristic. In inline mode the strip lives inside the
 * toolbar. When its natural width exceeds the available width, it asks
 * the parent (via onModeChange) to flip to wrap-above mode, which is
 * rendered as a flex-wrap row above the toolbar.
 *
 * The parent owns the mode state so the strip can be re-rendered in a
 * different DOM location when overflow flips.
 *
 * All-or-nothing wrap (rather than per-chip progressive overflow):
 * progressive overflow requires per-chip width measurement and order
 * tracking, and produces visually unstable output. All-or-nothing
 * produces predictable states. The sub-optimal "3 chips, 2 fit inline,
 * 1 doesn't" case is acceptable — they all wrap up.
 */

import { useEffect, useMemo, useRef } from 'react';
import { FileText, X } from 'lucide-react';
import { cn } from '@/utils/classNames';
import { isImageFile } from '@/utils/fileParser';

export type ChipStripMode = 'inline' | 'wrap-above';

export interface AttachmentChipStripProps {
  files: File[];
  onRemove: (index: number) => void;
  mode: ChipStripMode;
  onModeChange: (mode: ChipStripMode) => void;
}

export function AttachmentChipStrip({
  files,
  onRemove,
  mode,
  onModeChange,
}: AttachmentChipStripProps) {
  const stripRef = useRef<HTMLDivElement | null>(null);

  // Observe the strip's size; when its content overflows in inline
  // mode, request wrap-above. When in wrap-above mode the chips would
  // fit inline again, request inline. All-or-nothing — chips never
  // split across both modes.
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;

    const evaluate = () => {
      const overflow = el.scrollWidth > el.clientWidth;
      if (mode === 'inline' && overflow) {
        onModeChange('wrap-above');
      } else if (mode === 'wrap-above' && !overflow) {
        // In wrap-above mode the strip stretches to its parent's full
        // width, so scrollWidth ≤ clientWidth means the chips would
        // also fit if they were inline. (Imperfect heuristic — the
        // inline slot is narrower than the wrap-above row — but stable
        // enough; one extra resize tick re-evaluates if we guessed
        // wrong.)
        onModeChange('inline');
      }
    };

    evaluate();

    const observer = new ResizeObserver(evaluate);
    observer.observe(el);
    return () => observer.disconnect();
  }, [mode, files.length, onModeChange]);

  // Object URLs for image previews. Created synchronously via useMemo so the
  // <img> has a src on first paint; the matched cleanup effect revokes the
  // exact map it was paired with whenever `files` changes or on unmount,
  // so there's no desync or leak.
  const previewUrls = useMemo(() => {
    const map = new Map<File, string>();
    files.forEach((file) => {
      if (isImageFile(file)) {
        map.set(file, URL.createObjectURL(file));
      }
    });
    return map;
  }, [files]);

  useEffect(() => {
    return () => {
      for (const url of previewUrls.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, [previewUrls]);

  if (files.length === 0) return null;

  return (
    <div
      ref={stripRef}
      className={cn(
        'flex min-w-0 items-center gap-2',
        mode === 'wrap-above' ? 'flex-wrap' : ''
      )}
    >
      {files.map((file, index) => (
        <div
          key={`${file.name}-${index}`}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg',
            'bg-bg-secondary border border-border-default',
            'px-3 py-1 text-sm'
          )}
        >
          {isImageFile(file) ? (
            <img
              src={previewUrls.get(file)}
              alt={`Preview of ${file.name}`}
              className="h-6 w-6 flex-shrink-0 rounded object-cover"
            />
          ) : (
            <FileText
              className="h-4 w-4 flex-shrink-0 text-text-secondary"
              aria-hidden="true"
            />
          )}
          <span className="truncate max-w-[200px] text-text-primary">
            {file.name}
          </span>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className={cn(
              'flex-shrink-0 rounded p-0.5',
              'text-text-tertiary hover:text-error',
              'hover:bg-error-light dark:hover:bg-error-dark',
              'transition-colors'
            )}
            aria-label={`Remove ${file.name}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
